import Button from '@material-ui/core/Button'
import ChatBubbleOutline from '@material-ui/icons/ChatBubbleOutline'
import Skeleton from '@material-ui/lab/Skeleton'
import PropTypes from 'prop-types'
import NextLink from 'next/link'
import useStyles from './category-tabs.styles'

const TAB_ICONS = {
  'chat-bubble-outline': ChatBubbleOutline,
}

function CategoryTabs({
  items,
  value,
  onChange,
  ariaLabel,
  alignment = 'center',
  dense = false,
  className,
  getHref,
}) {
  const styles = useStyles({ alignment, dense })

  return (
    <div className={[styles.scrollOuter, className].filter(Boolean).join(' ')}>
      <div
        className={styles.root}
        role="tablist"
        aria-label={ariaLabel}
      >
        {items.map((item) => {
          if (item.variant === 'divider') {
            return (
              <span
                key={item.id}
                className={styles.tabDivider}
                role="presentation"
                aria-hidden="true"
              />
            )
          }

          if (item.variant === 'skeleton') {
            return (
              <Skeleton
                key={item.id}
                variant="rect"
                width={item.width || 80}
                height={45}
                style={{ borderRadius: 8, flexShrink: 0 }}
                aria-hidden="true"
              />
            )
          }

          const isSelected = item.id === value
          const IconComponent = item.icon ? TAB_ICONS[item.icon] : null
          const tabLabel = item.label || item.id
          const tabClassName = `${styles.tab} ${isSelected ? styles.active : ''} ${item.showIconOnly ? styles.tabIconOnly : ''}`
          const href = typeof getHref === 'function' ? getHref(item) : null

          // When a real href is supplied, render the tab as a `next/link`
          // anchor so the URL changes on click — good for SEO and middle-click
          // / new-tab — while still calling `onChange` for any side effects
          // callers rely on (analytics, scroll-to-anchor, controlled state).
          // Default behavior — pure local-state tab switching — is preserved
          // when `getHref` is omitted. We render `NextLink` directly rather
          // than wrapping a MUI `Button` because shared anchor wrappers in
          // this repo deliberately drop unknown props (role, aria-*, onClick),
          // and the visual styles already live on the `tab` className.
          if (href) {
            return (
              <NextLink
                key={item.id}
                href={href}
                onClick={onChange ? () => onChange(item.id) : undefined}
                role="tab"
                className={tabClassName}
                aria-selected={isSelected}
                aria-label={item.showIconOnly ? tabLabel : undefined}
                title={item.showIconOnly ? tabLabel : undefined}
              >
                {item.showIconOnly && IconComponent ? (
                  <IconComponent className={styles.tabLeadingIcon} fontSize="inherit" aria-hidden />
                ) : (
                  item.label ?? null
                )}
              </NextLink>
            )
          }

          return (
            <Button
              onClick={() => onChange(item.id)}
              key={item.id}
              role="tab"
              className={tabClassName}
              aria-selected={isSelected}
              aria-label={item.showIconOnly ? tabLabel : undefined}
              title={item.showIconOnly ? tabLabel : undefined}
            >
              {item.showIconOnly && IconComponent ? (
                <IconComponent className={styles.tabLeadingIcon} fontSize="inherit" aria-hidden />
              ) : (
                item.label ?? null
              )}
            </Button>
          )
        })}
      </div>
    </div>
  )
}

CategoryTabs.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string,
    variant: PropTypes.oneOf(['divider', 'skeleton']),
    width: PropTypes.number,
    showIconOnly: PropTypes.bool,
    icon: PropTypes.oneOf(['chat-bubble-outline']),
  })).isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  ariaLabel: PropTypes.string.isRequired,
  alignment: PropTypes.oneOf(['left', 'center']),
  dense: PropTypes.bool,
  className: PropTypes.string,
  /**
   * Optional builder that returns a real URL for an item. When supplied (and
   * non-null for a given item) the tab renders as a `next/link` anchor so the
   * URL changes on click, and `onChange` is still invoked for side effects.
   * When omitted, tabs behave as pure controlled in-place state (default).
   */
  getHref: PropTypes.func,
}

export default CategoryTabs
